/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { Avatar, Table, Input, Spin, Alert, FloatButton } from 'antd'
import { paragraphSmall, weightRegular } from '../../components/css'
import { SearchOutlined, ArrowUpOutlined } from '@ant-design/icons'
import OptionButtonIcon from 'src/components/List/OptionButtons/Icon'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import MoreOptions, {
  MoreOptionListItem,
} from '../../components/List/OptionButtons/More'
import useOrgList from './useOrgList'
import CustomOptionButtonFavourite from './components/CustomOptionButtonFavourite'
import colors from '@sportninja/common/constants/appColors'
import Icon from '../../components/Icon'
import useWindowSize from 'src/hooks/useWindowSize'
import { getTableStyles } from 'src/styles/tableStyles'
import Text from 'src/components/ts-components/Text'
import MembershipTypeFilter from 'src/pages/MySchedule/components/MembershipTypeFilter'

const SCROLL_THRESHOLD = 500

const renderGeneratePluginTag = ({ onClick }) => {
  return (
    <MoreOptionListItem
      onClick={() => {
        onClick()
      }}
      iconName='plug'
      faType='far'
      title={'Generate JS Plugin Tag'}
    />
  )
}

const OrgListTable = ({
  refreshKey,
  setCount = () => {},
  slug,
  setInitialState,
  onEditOrg,
  onClickWebPluginOnScheduleList = () => {},
  isFavorites = false,
  isMembership = false,
  specificNameColumn = 'name',
  onMembershipTypeChange = null,
  externalMembershipType,
  onMembershipTypeChangeExternal,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1200, [width])
  const {
    items,
    loading,
    hasMore,
    permissions,
    handleSearch,
    handleSort,
    handleMembershipType,
    loadMore,
    refresh,
    sort,
    direction,
    search,
    membershipType,
    error,
  } = useOrgList(slug, {
    initialSort: specificNameColumn,
    externalMembershipType,
    onMembershipTypeChangeExternal,
  })

  // Local state for managing favorites when isFavorites is true
  const [localFavorites, setLocalFavorites] = useState(new Set())

  // Ref to prevent multiple simultaneous loadMore calls
  const isLoadingMoreRef = useRef(false)

  // Ref to track initial mount and prevent refresh on first render
  const isInitialMountRef = useRef(true)

  // Refresh when refreshKey changes
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (refreshKey !== undefined) {
      refresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  // Initialize local favorites when items change and we're in favorites view
  useEffect(() => {
    if (isFavorites && items) {
      // Set all items as favorites initially when in favorites view
      const favoriteIds = new Set(items.map((item) => item.id))
      setLocalFavorites(favoriteIds)
    }
  }, [items, isFavorites])

  /**
   * Handle favorite state change for local management
   */
  const handleFavoriteChange = useCallback(
    (itemId, newFavoriteState) => {
      if (isFavorites) {
        setLocalFavorites((prev) => {
          const newSet = new Set(prev)
          if (newFavoriteState) {
            newSet.add(itemId)
          } else {
            newSet.delete(itemId)
          }
          return newSet
        })
      }
    },
    [isFavorites]
  )

  const handleScroll = useCallback(() => {
    // Prevent multiple calls if already loading or if we're already loading more
    if (loading || isLoadingMoreRef.current) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore) {
      isLoadingMoreRef.current = true
      loadMore()
    }
  }, [hasMore, loading, loadMore])

  // Reset loading ref when loading state changes to false
  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false
    }
  }, [loading])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const columns = useMemo(
    () => [
      {
        title: t('common:name'),
        key: specificNameColumn,
        dataIndex: specificNameColumn,
        sorter: true,
        ellipsis: true,
        showSorterTooltip: false,
        width: isMobile ? 160 : 'max-content',
        sortOrder:
          sort === specificNameColumn
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => (
          <Link
            to={`${ROUTES.ORG_ROOT}/${item.id}`}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              color: ${colors.NEUTRAL_0};
              &:hover {
                color: ${colors.PRIMARY};
              }
            `}
          >
            <Avatar
              src={
                getImageThumbnailId(item) ||
                item?.image?.url ||
                '/images/generic_placeholders/placeholder_organization.png'
              }
              icon={<Icon name='users' fontSize={14} />}
              shape='square'
              css={css`
                background-color: white;
                flex-shrink: 0;
                width: 32px !important;
                height: 32px !important;
                border-radius: 8px !important;
              `}
            />
            <div>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_0}
              >
                {item?.name || item?.name_full}
              </Text>
              <Text
                variant='paragraphXSmall'
                color={colors.NEUTRAL_50}
                weight='regular'
              >
                {item?.roles?.map((role) => role.role_name).join(' | ')}
              </Text>
            </div>
          </Link>
        ),
      },
      {
        title: isMobile
          ? 'Subs'
          : t('util:capitalize', {
              text: t('Web:subOrg_plural', { count: 2 }),
            }),
        key: 'children_count',
        dataIndex: 'children_count',
        sorter: false,
        showSorterTooltip: false,
        sortOrder:
          sort === 'children_count'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        width: isMobile ? 60 : 150,
      },
      {
        title: isMobile
          ? 'Comps'
          : t('util:capitalize', {
              text: t('common:competition_plural', { count: 2 }),
            }),
        key: 'schedules_count',
        dataIndex: 'schedules_count',
        sorter: false,
        showSorterTooltip: false,
        sortOrder:
          sort === 'schedules_count'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        width: isMobile ? 60 : 150,
      },
      {
        title: isMobile
          ? 'Teams'
          : t('util:capitalize', {
              text: t('common:team_plural', { count: 2 }),
            }),
        key: 'teams_count',
        dataIndex: 'teams_count',
        sorter: false,
        showSorterTooltip: false,
        sortOrder:
          sort === 'teams_count'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        width: isMobile ? 60 : 150,
      },
      {
        // title: 'Options',
        title: '',
        align: 'right',
        key: 'actions',
        width: isMobile ? 100 : 150,
        render: (_, item) => {
          const permission = permissions[item.id] || {}

          // Determine favorite state based on view type
          const favoriteState = isFavorites
            ? localFavorites.has(item.id)
            : item.is_favorite

          const moreOptions = []
          if (permission.update) {
            moreOptions.push({
              key: 'plugin',
              Component: renderGeneratePluginTag,
              componentProps: {
                Button: renderGeneratePluginTag,
                schedule: item,
                scheduleId: item.id,
                onClick: () => {
                  onClickWebPluginOnScheduleList(item)
                },
              },
            })
          }
          if (permission.delete && item.parent_id !== null) {
            moreOptions.push({
              key: 'delete',
              Component: OptionButtonDelete,
              componentProps: {
                useForceDelete: true,
                unmountOnClose: true,
                entityType: ENTITY_TYPES.org,
                itemId: item.id,
                title: `Delete ${item.name_full}`,
                type: t('common:organization'),
                iconName: 'trash',
                onComplete: () => {
                  window.location.reload()
                },
              },
            })
          }

          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                justify-content: flex-end;
              `}
            >
              {permission.update && (
                <OptionButtonIcon
                  name='pencil'
                  onClick={() => {
                    setInitialState(item)
                    onEditOrg()
                  }}
                />
              )}
              <CustomOptionButtonFavourite
                entityType={ENTITY_TYPES.org}
                item={item}
                isFavourite={favoriteState}
                isFavoritesView={isFavorites}
                onFavoriteChange={(newState) =>
                  handleFavoriteChange(item.id, newState)
                }
              />
              {moreOptions.length > 0 && <MoreOptions options={moreOptions} />}
            </div>
          )
        },
      },
    ],
    [
      sort,
      direction,
      permissions,
      isFavorites,
      localFavorites,
      onEditOrg,
      setInitialState,
      onClickWebPluginOnScheduleList,
      handleFavoriteChange,
      specificNameColumn,
      isMobile,
    ]
  )

  return (
    <div
      css={css`
        width: 100%;
        @media (max-width: 768px) {
          display: flex;
          flex-direction: column;
        }
      `}
    >
      <div
        css={css`
          margin-top: 16px;
          margin-bottom: 16px;
          display: flex;
          justify-content: flex-start;
          align-items: flex-start;
          @media (max-width: 768px) {
            margin: 0 16px;
            padding: 16px 0;
            flex-direction: column;
            width: auto;
            gap: 16px;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            @media (max-width: 768px) {
              width: 100%;
            }
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_0}
          >
            Search
          </Text>
          <Input
            className='list-search-input'
            placeholder={t('common:search')}
            prefix={
              <SearchOutlined
                css={css`
                  font-size: 16px;
                  color: ${colors.NEUTRAL_50};
                `}
              />
            }
            suffix={loading && items.length > 0 ? <Spin size='small' /> : null}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear={true}
            css={css`
              ${paragraphSmall}
              ${weightRegular}
            width: 300px;
              color: ${colors.WHITE};
              border-radius: 8px;
              background: transparent;
              height: 40px !important;
              margin-right: 16px;
              .ant-input {
                background-color: transparent;
                color: ${colors.WHITE};
              }
              .ant-input-prefix {
                color: ${colors.NEUTRAL_100};
              }
              &::placeholder {
                color: ${colors.NEUTRAL_100};
              }
              @media (max-width: 768px) {
                width: 100%;
              }
              // remove input hover background
              &:hover {
                background-color: transparent;
              }
            `}
          />
        </div>
        {isMembership && (
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 8px;
              max-width: 155px;
              align-self: flex-start;
              @media (max-width: 768px) {
                max-width: 100%;
                width: 100%;
              }
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_0}
            >
              Membership Type
            </Text>
            <MembershipTypeFilter
              selected={membershipType}
              onSelect={(value) => {
                handleMembershipType(value)
                // Only call legacy callback if new controlled callback is not provided
                if (!onMembershipTypeChangeExternal) {
                  onMembershipTypeChange?.(value)
                }
              }}
            />
          </div>
        )}
      </div>
      {error ? (
        <Alert
          message={error}
          type='error'
          showIcon
          css={css`
            margin-bottom: 16px;
            margin-top: 16px;
          `}
        />
      ) : null}
      {loading && items.length === 0 ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={items}
          rowKey='id'
          pagination={false}
          onChange={(pagination, filters, sorter) => {
            if (sorter.order) {
              // For columns other than name, start with descending order (greater to smaller)
              if (
                sorter.field !== specificNameColumn &&
                sorter.order === 'ascend'
              ) {
                handleSort(sorter.field, 'desc')
              } else {
                handleSort(
                  sorter.field,
                  sorter.order === 'ascend' ? 'asc' : 'desc'
                )
              }
            } else {
              handleSort(specificNameColumn, 'asc') // Default sort
            }
          }}
          size='small'
          scroll={{ x: isMobile ? 600 : width < 1200 ? 1000 : undefined }}
          css={getTableStyles(isMobile)}
        />
      )}
      {loading && items.length > 0 && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 20px;
          `}
        >
          <Spin size='small' color={colors.PRIMARY} />
        </div>
      )}
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </div>
  )
}

export default OrgListTable
